import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import TimeInput from '../components/TimeInput';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Repeat,
  Calendar,
  Loader2,
  Trash2,
  Edit,
  Eye,
  ChevronRight,
  Clock,
  MapPin,
  Building2,
  Truck,
  CheckCircle,
  PauseCircle,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import StatTile from '../components/StatTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom Interval' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const WEEK_OF_MONTH = [
  { value: 1, label: '1st' },
  { value: 2, label: '2nd' },
  { value: 3, label: '3rd' },
  { value: 4, label: '4th' },
  { value: -1, label: 'Last' },
];

const END_TYPE_OPTIONS = [
  { value: 'never', label: 'Never (ongoing)' },
  { value: 'after_occurrences', label: 'After X occurrences' },
  { value: 'on_date', label: 'On specific date' },
];

export default function RecurringJobs() {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSeriesDialog, setShowSeriesDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesJobs, setSeriesJobs] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Preview state
  const [previewDates, setPreviewDates] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [savingEquipment, setSavingEquipment] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    is_internal: false,  // Toggle for internal vs client job
    client_name: '',
    contact_name: '',
    phone: '',
    email: '',
    service_address: '',
    job_title: '',
    service_type: '',  // For client jobs
    equipment_id: '',  // For internal jobs - selected equipment
    equipment_name: '',  // For internal jobs - equipment name
    custom_equipment: '',  // For manually entered equipment
    time: '',
    duration: '',
    priority: 'Normal',
    notes: '',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    frequency: 'weekly',
    interval: 1,
    days_of_week: [],
    day_of_month: null,
    week_of_month: null,
    end_type: 'never',
    end_after_occurrences: 10,
    end_date: '',
    instances_to_create: 10
  });

  const fetchData = useCallback(async () => {
    try {
      const [servicesRes, contactsRes, equipmentRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/equipment`)
      ]);
      setServices(servicesRes.data);
      setContacts(contactsRes.data);
      setEquipmentList(equipmentRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchRecurringSeries = useCallback(async () => {
    try {
      // Get all recurring jobs and group by series
      const response = await axios.get(`${API}/jobs`);
      const recurringJobs = response.data.filter(j => j.recurring_series_id);
      
      // Group by series_id
      const seriesMap = {};
      recurringJobs.forEach(job => {
        const sid = job.recurring_series_id;
        if (!seriesMap[sid]) {
          seriesMap[sid] = {
            series_id: sid,
            job_title: job.job_title,
            client_name: job.client_name,
            service_type: job.service_type,
            recurrence_pattern: job.recurrence_pattern,
            jobs: []
          };
        }
        seriesMap[sid].jobs.push(job);
      });
      
      // Sort jobs within each series by date
      Object.values(seriesMap).forEach(s => {
        s.jobs.sort((a, b) => a.date.localeCompare(b.date));
        s.next_date = s.jobs.find(j => j.date >= format(new Date(), 'yyyy-MM-dd'))?.date;
        s.total_jobs = s.jobs.length;
      });
      
      setSeries(Object.values(seriesMap));
    } catch (error) {
      console.error('Failed to fetch recurring series:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchRecurringSeries();
  }, [fetchData, fetchRecurringSeries]);

  // Preview dates when recurrence settings change
  const fetchPreview = async () => {
    if (!form.start_date || !form.frequency) return;
    
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        start_date: form.start_date,
        frequency: form.frequency,
        interval: form.interval.toString(),
        end_type: form.end_type,
        preview_count: '10'
      });
      
      if (form.days_of_week.length > 0) {
        params.append('days_of_week', form.days_of_week.join(','));
      }
      if (form.day_of_month) {
        params.append('day_of_month', form.day_of_month.toString());
      }
      if (form.week_of_month) {
        params.append('week_of_month', form.week_of_month.toString());
      }
      if (form.end_type === 'after_occurrences') {
        params.append('end_after_occurrences', form.end_after_occurrences.toString());
      }
      if (form.end_type === 'on_date' && form.end_date) {
        params.append('end_date', form.end_date);
      }
      
      const response = await axios.get(`${API}/recurring-jobs/preview-dates?${params}`);
      setPreviewDates(response.data.dates || []);
    } catch (error) {
      console.error('Failed to preview dates:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (showCreateDialog) {
      const timeout = setTimeout(fetchPreview, 500);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.start_date, form.frequency, form.interval, form.days_of_week, form.day_of_month, form.week_of_month, form.end_type, form.end_after_occurrences, form.end_date, showCreateDialog]);

  const handleDayToggle = (day) => {
    setForm(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort()
    }));
  };

  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      // Get primary contact from nested contacts array
      const primaryContact = contact.contacts?.find(c => c.is_primary) || contact.contacts?.[0];
      
      // Build address from physical address fields
      const address = [
        contact.physical_street,
        contact.physical_city,
        contact.physical_state,
        contact.physical_zip
      ].filter(Boolean).join(', ');
      
      setForm(prev => ({
        ...prev,
        client_name: contact.client_name || contact.company_name || '',
        contact_name: primaryContact?.name || '',
        phone: primaryContact?.phone || '',
        email: primaryContact?.email || '',
        service_address: address
      }));
    }
  };

  const handleEquipmentSelect = (equipmentId) => {
    const equipment = equipmentList.find(e => e.id === equipmentId);
    if (equipment) {
      setForm(prev => ({
        ...prev,
        equipment_id: equipment.id,
        equipment_name: equipment.name,
        custom_equipment: ''
      }));
    }
  };

  const handleAddEquipment = async () => {
    if (!newEquipmentName.trim()) {
      toast.error('Please enter equipment name');
      return;
    }
    
    setSavingEquipment(true);
    try {
      const response = await axios.post(`${API}/equipment`, { 
        name: newEquipmentName.trim(),
        type: 'Other',
        status: 'Active'
      });
      toast.success('Equipment added');
      setNewEquipmentName('');
      setShowAddEquipmentDialog(false);
      // Refresh equipment list
      const equipmentRes = await axios.get(`${API}/equipment`);
      setEquipmentList(equipmentRes.data);
      // Select the new equipment
      setForm(prev => ({ 
        ...prev, 
        equipment_id: response.data.id,
        equipment_name: newEquipmentName.trim(),
        custom_equipment: ''
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setSavingEquipment(false);
    }
  };

  const handleCreate = async () => {
    // Validate required fields based on job type
    if (!form.job_title || !form.start_date) {
      toast.error('Please fill in required fields (Job Title, Start Date)');
      return;
    }
    
    // For client jobs, require client name and service type
    if (!form.is_internal && !form.client_name) {
      toast.error('Please select or enter a client name');
      return;
    }
    
    // For client jobs, require service type
    if (!form.is_internal && !form.service_type) {
      toast.error('Please select a service type');
      return;
    }
    
    // For internal jobs, require equipment selection
    if (form.is_internal && !form.equipment_name && !form.custom_equipment) {
      toast.error('Please select or enter equipment');
      return;
    }
    
    setSaving(true);
    try {
      // For internal jobs, use equipment name as service_type for tracking
      const serviceType = form.is_internal 
        ? `Equipment: ${form.equipment_name || form.custom_equipment}` 
        : form.service_type;
      
      const payload = {
        client_name: form.is_internal ? 'Internal' : form.client_name,
        contact_name: form.contact_name,
        phone: form.phone,
        email: form.email,
        service_address: form.service_address,
        job_title: form.job_title,
        service_type: serviceType,
        time: form.time || null,
        duration: form.duration ? parseFloat(form.duration) : null,
        priority: form.priority,
        notes: form.is_internal && (form.equipment_name || form.custom_equipment)
          ? `Equipment: ${form.equipment_name || form.custom_equipment}${form.notes ? '\n\n' + form.notes : ''}`
          : form.notes,
        start_date: form.start_date,
        recurrence_config: {
          frequency: form.frequency,
          interval: form.interval,
          days_of_week: form.days_of_week.length > 0 ? form.days_of_week : null,
          day_of_month: form.day_of_month,
          week_of_month: form.week_of_month,
          end_type: form.end_type,
          end_after_occurrences: form.end_type === 'after_occurrences' ? form.end_after_occurrences : null,
          end_date: form.end_type === 'on_date' ? form.end_date : null
        },
        instances_to_create: form.instances_to_create
      };
      
      const response = await axios.post(`${API}/recurring-jobs`, payload);
      toast.success(`Created ${response.data.jobs.length} recurring jobs!`);
      setShowCreateDialog(false);
      resetForm();
      fetchRecurringSeries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create recurring jobs');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setForm({
      is_internal: false,
      client_name: '',
      contact_name: '',
      phone: '',
      email: '',
      service_address: '',
      job_title: '',
      service_type: '',
      equipment_id: '',
      equipment_name: '',
      custom_equipment: '',
      time: '',
      duration: '',
      priority: 'Normal',
      notes: '',
      start_date: format(new Date(), 'yyyy-MM-dd'),
      frequency: 'weekly',
      interval: 1,
      days_of_week: [],
      day_of_month: null,
      week_of_month: null,
      end_type: 'never',
      end_after_occurrences: 10,
      end_date: '',
      instances_to_create: 10
    });
    setPreviewDates([]);
  };

  const handleViewSeries = async (seriesData) => {
    setSelectedSeries(seriesData);
    setSeriesJobs(seriesData.jobs);
    setShowSeriesDialog(true);
  };

  const handleDeleteSeries = async () => {
    if (!selectedSeries) return;
    
    try {
      await axios.delete(`${API}/recurring-jobs/series/${selectedSeries.series_id}`);
      toast.success('Recurring series deleted');
      setShowDeleteDialog(false);
      setShowSeriesDialog(false);
      setSelectedSeries(null);
      fetchRecurringSeries();
    } catch (error) {
      toast.error('Failed to delete series');
    }
  };

  const handleExtendSeries = async () => {
    if (!selectedSeries) return;
    
    try {
      const response = await axios.post(`${API}/recurring-jobs/series/${selectedSeries.series_id}/extend?additional_instances=10`);
      toast.success(`Added ${response.data.jobs.length} more instances`);
      fetchRecurringSeries();
      // Refresh series jobs
      const updatedSeries = await axios.get(`${API}/recurring-jobs/series/${selectedSeries.series_id}`);
      setSeriesJobs(updatedSeries.data.jobs);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend series');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 animate-pulse rounded" />
        <div className="h-64 bg-slate-200 animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recurring-jobs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Recurring Jobs
          </h1>
          <p className="text-slate-500 mt-1">Schedule jobs that repeat on a regular basis</p>
        </div>
        <Button
          size="sm"
          className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
          onClick={() => setShowCreateDialog(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Recurring Job
        </Button>
      </div>

      {/* Series List */}
      {series.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Repeat className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No recurring jobs</h3>
            <p className="text-slate-500 mb-4">Create recurring jobs for regular work</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Recurring Job
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {series.map((s) => (
            <Card 
              key={s.series_id} 
              className="border-slate-200 hover:border-amber-300 transition-colors cursor-pointer"
              onClick={() => handleViewSeries(s)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{s.job_title}</h3>
                    <p className="text-sm text-slate-500 truncate">{s.client_name}</p>
                  </div>
                  <Badge variant="outline" className="ml-2 shrink-0">
                    <Repeat className="w-3 h-3 mr-1" />
                    {s.total_jobs}
                  </Badge>
                </div>
                
                <div className="mt-3 space-y-1">
                  <div className="flex items-center text-sm text-slate-600">
                    <Clock className="w-4 h-4 mr-2 text-slate-400" />
                    {s.recurrence_pattern}
                  </div>
                  {s.next_date && (
                    <div className="flex items-center text-sm text-slate-600">
                      <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                      Next: {format(parseISO(s.next_date), 'MMM d, yyyy')}
                    </div>
                  )}
                </div>
                
                <div className="mt-3 flex items-center justify-end text-amber-600 text-sm font-medium">
                  View Details
                  <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              Create Recurring Job
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Job Type Toggle */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_internal"
                  checked={form.is_internal}
                  onCheckedChange={(checked) => setForm(prev => ({ 
                    ...prev, 
                    is_internal: checked,
                    client_name: checked ? '' : prev.client_name
                  }))}
                />
                <Label htmlFor="is_internal" className="cursor-pointer">
                  Internal Job (no client)
                </Label>
              </div>
              <span className="text-sm text-slate-500">
                e.g., "Take truck to be serviced", "Office cleaning"
              </span>
            </div>

            {/* Client Selection - Only show if NOT internal */}
            {!form.is_internal && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Client Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select from CRM</Label>
                    <Select onValueChange={handleContactSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose from contacts..." />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.client_name || c.company_name || 'Unknown'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Client Name *</Label>
                    <Input
                      value={form.client_name}
                      onChange={(e) => setForm(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Company or client name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Service Address</Label>
                    <Input
                      value={form.service_address}
                      onChange={(e) => setForm(prev => ({ ...prev, service_address: e.target.value }))}
                      placeholder="Address"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Phone number"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Job Details */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900">Job Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Job Title *</Label>
                  <Input
                    value={form.job_title}
                    onChange={(e) => setForm(prev => ({ ...prev, job_title: e.target.value }))}
                    placeholder={form.is_internal ? "e.g., Truck service, Oil change, Equipment inspection" : "e.g., Weekly Inspection"}
                  />
                </div>
                
                {/* Service Type for Client Jobs */}
                {!form.is_internal && (
                  <div className="space-y-2">
                    <Label>Service Type *</Label>
                    <Select 
                      value={form.service_type}
                      onValueChange={(v) => setForm(prev => ({ ...prev, service_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map(s => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Equipment Selection for Internal Jobs */}
                {form.is_internal && (
                  <>
                    <div className="space-y-2">
                      <Label>Select Equipment *</Label>
                      <div className="flex gap-2">
                        <Select 
                          value={form.equipment_id}
                          onValueChange={handleEquipmentSelect}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Choose equipment..." />
                          </SelectTrigger>
                          <SelectContent>
                            {equipmentList.map(e => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.name} {e.type && `(${e.type})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setShowAddEquipmentDialog(true)}
                          title="Add new equipment"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-slate-500 text-sm">Or enter equipment manually:</Label>
                      <Input
                        value={form.custom_equipment}
                        onChange={(e) => setForm(prev => ({ 
                          ...prev, 
                          custom_equipment: e.target.value, 
                          equipment_id: '',
                          equipment_name: ''
                        }))}
                        placeholder="e.g., Rental truck, Borrowed excavator"
                      />
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label>Time</Label>
                  <TimeInput
                    value={form.time}
                    onChange={(val) => setForm(prev => ({ ...prev, time: val }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Duration (hours)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={form.duration}
                    onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder="e.g., 2"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Job notes..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Recurrence Settings */}
            <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="font-semibold text-amber-900 flex items-center gap-2">
                <Repeat className="w-4 h-4" />
                Recurrence Pattern
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Frequency *</Label>
                  <Select 
                    value={form.frequency}
                    onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v, days_of_week: [], day_of_month: null, week_of_month: null }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Weekly/Biweekly day selection */}
                {(form.frequency === 'weekly' || form.frequency === 'biweekly') && (
                  <div className="space-y-2 md:col-span-2">
                    <Label>Days of Week</Label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map(day => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => handleDayToggle(day.value)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            form.days_of_week.includes(day.value)
                              ? 'bg-amber-500 text-slate-900'
                              : 'bg-white border border-slate-300 text-slate-700 hover:border-amber-400'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Monthly settings */}
                {form.frequency === 'monthly' && (
                  <>
                    <div className="space-y-2">
                      <Label>Day of Month</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={form.day_of_month || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, day_of_month: e.target.value ? parseInt(e.target.value) : null, week_of_month: null }))}
                        placeholder="e.g., 15"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Or: Week of Month</Label>
                      <div className="flex gap-2">
                        <Select 
                          value={form.week_of_month?.toString() || ''}
                          onValueChange={(v) => setForm(prev => ({ ...prev, week_of_month: parseInt(v), day_of_month: null }))}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Week" />
                          </SelectTrigger>
                          <SelectContent>
                            {WEEK_OF_MONTH.map(w => (
                              <SelectItem key={w.value} value={w.value.toString()}>{w.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select 
                          value={form.days_of_week[0]?.toString() || ''}
                          onValueChange={(v) => setForm(prev => ({ ...prev, days_of_week: [parseInt(v)] }))}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Day" />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS_OF_WEEK.map(d => (
                              <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Custom interval */}
                {form.frequency === 'custom' && (
                  <div className="space-y-2">
                    <Label>Every X Days</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.interval ?? ''}
                      onChange={(e) => setForm(prev => ({ ...prev, interval: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    />
                  </div>
                )}
                
                {/* End conditions */}
                <div className="space-y-2">
                  <Label>Ends</Label>
                  <Select 
                    value={form.end_type}
                    onValueChange={(v) => setForm(prev => ({ ...prev, end_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {END_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {form.end_type === 'after_occurrences' && (
                  <div className="space-y-2">
                    <Label>Number of Occurrences</Label>
                    <Input
                      type="number"
                      min="1"
                      value={form.end_after_occurrences ?? ''}
                      onChange={(e) => setForm(prev => ({ ...prev, end_after_occurrences: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    />
                  </div>
                )}
                
                {form.end_type === 'on_date' && (
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Jobs to Create Now</Label>
                  <Input
                    type="number"
                    min="1"
                    max="52"
                    value={form.instances_to_create}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow empty field while typing, will validate on blur/submit
                      setForm(prev => ({ ...prev, instances_to_create: val === '' ? '' : parseInt(val) }));
                    }}
                    onBlur={(e) => {
                      // Ensure valid value on blur (minimum 1)
                      const val = parseInt(e.target.value);
                      if (!val || val < 1) {
                        setForm(prev => ({ ...prev, instances_to_create: 1 }));
                      } else if (val > 52) {
                        setForm(prev => ({ ...prev, instances_to_create: 52 }));
                      }
                    }}
                  />
                  <p className="text-xs text-slate-500">You can extend the series later</p>
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4 p-3 bg-white rounded border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Preview Dates</Label>
                  {loadingPreview && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
                </div>
                <div className="flex flex-wrap gap-2">
                  {previewDates.slice(0, 10).map((date, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {format(parseISO(date), 'MMM d')}
                    </Badge>
                  ))}
                  {previewDates.length > 10 && (
                    <Badge variant="outline" className="text-xs">+{previewDates.length - 10} more</Badge>
                  )}
                  {previewDates.length === 0 && !loadingPreview && (
                    <span className="text-sm text-slate-500">Configure recurrence to see dates</span>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Create {form.instances_to_create || 1} Jobs
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Series Detail Dialog */}
      <Dialog open={showSeriesDialog} onOpenChange={setShowSeriesDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl">
              {selectedSeries?.job_title}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSeries && (
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600">{selectedSeries.client_name}</p>
                  <p className="text-sm text-amber-600">{selectedSeries.recurrence_pattern}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleExtendSeries}>
                    <Plus className="w-4 h-4 mr-1" />
                    Extend
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
              
              <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                {seriesJobs.map(job => (
                  <div key={job.id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-amber-600">{job.job_number}</span>
                        <Badge 
                          variant="outline" 
                          className={
                            job.status === 'Completed' ? 'bg-green-50 text-green-700' :
                            job.status === 'In Progress' ? 'bg-blue-50 text-blue-700' :
                            ''
                          }
                        >
                          {job.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        {format(parseISO(job.date), 'EEEE, MMM d, yyyy')}
                        {job.time && ` at ${job.time}`}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(`/jobs?id=${job.id}`, '_blank')}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Series?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all future scheduled jobs in this series. Completed past jobs will be kept.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSeries}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Series
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add New Equipment Dialog */}
      <Dialog open={showAddEquipmentDialog} onOpenChange={setShowAddEquipmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Equipment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Equipment Name</Label>
              <Input
                value={newEquipmentName}
                onChange={(e) => setNewEquipmentName(e.target.value)}
                placeholder="e.g., Truck #4, New Excavator"
              />
            </div>
            <p className="text-sm text-slate-500">
              This will add the equipment to your inventory in Services → Equipment
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => { setShowAddEquipmentDialog(false); setNewEquipmentName(''); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddEquipment}
                disabled={savingEquipment || !newEquipmentName.trim()}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              >
                {savingEquipment && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Add Equipment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
